import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BreadcrumbLinkInterface,
  DalState,
  EffectFeedbackActions,
  EffectFeedbackInterface,
  EffectFeedbackQueries,
  getRouterState,
  PageHeaderInterface,
  Permissions,
  PersonInterface,
  RouterStateUrl,
  SchoolInterface,
  SchoolQueries,
  TaskEduContentInterface,
  TaskEduContentQueries,
  TaskInstance,
  TaskInstanceQueries,
  TaskInterface,
  TaskQueries,
  UiActions,
  UiQuery,
  UserQueries,
} from '@campus/dal';
import {
  FeedBackServiceInterface,
  FEEDBACK_SERVICE_TOKEN,
  GameLearningEventsService,
  NavigationItemServiceInterface,
  NAVIGATION_ITEM_SERVICE_TOKEN,
  StudentTaskServiceInterface,
  STUDENT_TASK_SERVICE_TOKEN,
} from '@campus/shared';
import { DropdownMenuItemInterface, NavItem } from '@campus/ui';
import { Dictionary } from '@ngrx/entity';
import { RouterReducerState } from '@ngrx/router-store';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AppViewModel implements OnDestroy {
  //presentation streams
  public sideNavOpen$: Observable<boolean>;
  public bannerFeedback$: Observable<EffectFeedbackInterface>;
  public pageHeaderDetails$: Observable<PageHeaderInterface>;
  public userPermissions$: Observable<string[]>;

  public denseMenu$: Observable<boolean>;
  public breadcrumbs$: Observable<BreadcrumbLinkInterface[]>;
  public sideNavItems$: Observable<NavItem[]>;

  public schools$: Observable<SchoolInterface[]>;
  public currentSchool$: Observable<SchoolInterface>;
  public currentUser$: Observable<PersonInterface>;
  public avatarMenuItems$: Observable<DropdownMenuItemInterface[]>;

  // intermediate  streams
  private isLoggedIn$: Observable<boolean>;
  private routerState$: Observable<RouterReducerState<RouterStateUrl>>;

  private subscriptions: Subscription;

  private store: Store<DalState> = inject(Store);
  private navItemService: NavigationItemServiceInterface = inject(NAVIGATION_ITEM_SERVICE_TOKEN);
  private gameLearningEventsService = inject(GameLearningEventsService);
  private feedbackService: FeedBackServiceInterface = inject<FeedBackServiceInterface>(FEEDBACK_SERVICE_TOKEN);
  private breakPointObserver: BreakpointObserver = inject(BreakpointObserver);
  private studentTaskService: StudentTaskServiceInterface = inject(STUDENT_TASK_SERVICE_TOKEN);
  private router: Router = inject(Router);

  constructor() {
    this.setSourceStream();
    this.initialize();
  }

  // sets sideNav opened state
  public toggleSidebar(open: boolean) {
    this.store.dispatch(new UiActions.ToggleNavOpen({ open }));
  }

  // event handler for feedback dismiss
  // used by banner and snackbar
  public onFeedbackDismiss(event: { action: Action; feedbackId: string }): void {
    const payload: { id: string; userAction?: Action } = {
      id: event.feedbackId,
    };
    if (event.action) {
      this.store.dispatch(event.action);
      payload.userAction = event.action;
    }

    this.store.dispatch(new EffectFeedbackActions.DeleteEffectFeedback(payload));
  }

  public onNavItemChanged(navItem: NavItem) {
    this.store.dispatch(new UiActions.UpdateNavItem({ navItem }));
  }

  public toggleSidebarOnNavigation() {
    //Hide sidebar on mobile if we navigate or change screensize
    this.subscriptions.add(
      this.store
        .pipe(
          select(getRouterState),
          switchMap(() => {
            return this.breakPointObserver
              .observe([Breakpoints.XSmall, Breakpoints.Small])
              .pipe(map((result) => result.matches));
          })
        )
        .subscribe((isMobile) => {
          this.toggleSidebar(!isMobile);
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private initialize() {
    this.gameLearningEventsService.registerEventListener();
    this.setNavItems();
    this.setFeedbackFlow();

    this.denseMenu$ = this.store.select(UiQuery.getDenseMenu);
    this.breadcrumbs$ = this.store.pipe(select(UiQuery.getBreadcrumbs));

    this.subscriptions = new Subscription();
  }

  private setNavItems() {
    // send data to service -> get array of navItems
    this.sideNavItems$ = combineLatest([
      this.userPermissions$,
      this.isLoggedIn$,
      this.store.select(TaskQueries.getAll),
      this.store.select(TaskInstanceQueries.getAllGroupedByTaskId),
      this.store.select(TaskEduContentQueries.getAllGroupedByTaskId),
      this.currentSchool$,
      this.router.events.pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter((event) => event instanceof NavigationEnd)
      ),
    ]).pipe(
      map(
        ([permissions, isLoggedIn, tasks, taskInstanceDict, taskEduContentDict, currentSchool, _]: [
          string[],
          boolean,
          TaskInterface[],
          Dictionary<TaskInstance[]>,
          Dictionary<TaskEduContentInterface[]>,
          SchoolInterface,
          NavigationEnd
        ]) => {
          if (!isLoggedIn) return [];

          const openTasks = tasks
            .filter((task) => (taskInstanceDict[task.id] || []).some((ti) => ti.isOpen))
            .map((task) => ({
              ...task,
              taskInstances: taskInstanceDict[task.id] || [],
              taskEduContents: taskEduContentDict[task.id] || [],
            }))
            .filter((task) => !this.studentTaskService.isTaskFinished(task));

          const currentRoute = this.router.url;

          return this.navItemService
            .getNavItemsForTree('sideNav', permissions, undefined, undefined, currentRoute)
            .map((navItem) => {
              navItem.link = this.convertNavItemLink(navItem.link, currentSchool?.id);

              return {
                ...navItem,
                ...(permissions.includes(Permissions.tasks.OPEN_POLPO_TASKS) &&
                navItem.link?.toString().startsWith('/tasks')
                  ? { badge: openTasks.length.toString() }
                  : {}),
              };
            });
        }
      )
    );

    // get sideNav opened state
    this.sideNavOpen$ = this.store.pipe(select(UiQuery.getNavOpen));
  }

  private convertNavItemLink(
    navItemLink: string | string[] | (() => string | string[]),
    currentSchoolId: number
  ): string {
    let link = typeof navItemLink === 'function' ? navItemLink() : navItemLink;

    if (Array.isArray(link)) link = link.join('/');

    // Replace placeholders in the link
    if (currentSchoolId) link = link.replace('{:schoolId}', currentSchoolId.toString());

    return link;
  }

  private setSourceStream() {
    this.routerState$ = this.store.pipe(select(getRouterState));
    this.pageHeaderDetails$ = this.store.select(UiQuery.getHeaderDetails);

    this.userPermissions$ = this.store.pipe(select(UserQueries.getPermissions));

    this.isLoggedIn$ = this.store.pipe(
      select(UserQueries.getCurrentUser),
      map((user) => !!user)
    );

    this.schools$ = this.store.pipe(select(SchoolQueries.getAll));
    this.currentSchool$ = this.getCurrentSchool$();
    this.currentUser$ = this.store.pipe(select(UserQueries.getCurrentUser));
  }

  private setFeedbackFlow() {
    // success feedback goes to the feedbackService -> snackbar
    // success feedback goes to the feedbackService -> snackbar
    this.store
      .pipe(
        select(EffectFeedbackQueries.getNextSuccess),
        map((feedback) => this.feedbackService.openSnackbar(feedback)),
        filter((snackbar) => !!snackbar),
        switchMap((snackbarInfo) => this.feedbackService.snackbarAfterDismiss(snackbarInfo)),
        map((event) => ({
          action: event.actionToDispatch,
          feedbackId: event.feedback.id,
        }))
      )
      .subscribe((evt) => this.onFeedbackDismiss(evt));

    // error feedback goes into a stream -> bannerComponent
    this.bannerFeedback$ = this.store.pipe(
      select(EffectFeedbackQueries.getNextError),
      map(this.feedbackService.addDefaultCancelButton)
    );
  }

  private getCurrentSchool$(): Observable<SchoolInterface> {
    return combineLatest([
      this.schools$,
      this.routerState$.pipe(distinctUntilChanged((a, b) => a.state.params.id === b.state.params.id)),
    ]).pipe(
      map(([schools, routerState]) => {
        const schoolId = +routerState.state.params.id;

        return schools.find((s) => s.id === schoolId);
      })
    );
  }
}
